import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { I18nModule, ProductSearchPage } from '@spartacus/core';
import { FormErrorsModule } from '@spartacus/storefront';
import { Subscription, take } from 'rxjs';
import { QuoteProductListService } from '../product-list/core/quote-product-list.service';
import { AmorphSelectComponent } from '../../../../../../components/form-elements/amorph-select/amorph-select.component';
import { AdvancedSearchService } from './core/advanced-search.service';
import { CustomValidatorService } from '../../../../../../components/form-elements/custom-validator.service';
import { QuoteSearchProductService } from '../_core/search-products.service';
import { Option } from '../../../../../../core/models/option.model';
import { loadingStatus, successStatus } from '../../../../../../core/models/request-status.model';
import { isEmpty } from '../../../../../../core/services/simple-tools';
import { LoaderService } from '../../../../../../components/loader/loader.service';
import { LOADER_TYPE } from '../../../../../../components/loader/loader.model';

@Component({
  selector: 'advanced-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, I18nModule, FormErrorsModule, AmorphSelectComponent],
  templateUrl: './advanced-search.component.html',
  styleUrl: './advanced-search.component.scss',
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {
  @ViewChild('speedRPM') $speedRPMContainer!: ElementRef;
  @ViewChild('outputHP') $outputHPContainer!: ElementRef;
  @ViewChild('voltage') $voltageContainer!: ElementRef;

  subscription = new Subscription();

  form: UntypedFormGroup = this.fb.group({
    speedRPM: ['', [this.customValidatorService.required()]],

    outputHP: ['', [this.customValidatorService.required()]],
    voltage: ['', [this.customValidatorService.required()]],
    frameSize: ['', []],

    outputKW: ['', []],
    mounting: ['', []],
    phase: ['', []],
    enclosure: ['', []],

    application: ['', []],
  });

  page$ = this.quoteSearchProductService.page$;
  speedRPMs$ = this.advancedSearchService.speedRPMs$;
  outputHPs$ = this.advancedSearchService.outputHPs$;
  voltages$ = this.advancedSearchService.voltages$;
  frameSizes$ = this.advancedSearchService.frameSizes$;
  outputKWs$ = this.advancedSearchService.outputKWs$;
  mountings$ = this.advancedSearchService.mountings$;
  phases$ = this.advancedSearchService.phases$;
  enclosures$ = this.advancedSearchService.enclosures$;

  speedRPMInputType = 'select';
  outputHPInputType = 'select';
  voltageInputType = 'select';
  frameSizeInputType = 'select';

  updateOptions = true;

  constructor(
    protected fb: UntypedFormBuilder,
    private quoteProductListService: QuoteProductListService,
    private advancedSearchService: AdvancedSearchService,
    private customValidatorService: CustomValidatorService,
    private quoteSearchProductService: QuoteSearchProductService,
    private loaderService: LoaderService,
  ) {}

  ngOnInit(): void {
    this.clear();
    this.handlerSpeedRPMFormValue();
    this.handlerOutputKWFormValue();
    this.handlerVoltageFormValue();
    this.handlerFrameSizeFormValue();
    this.handlerClear();
    this.checkPage();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.quoteProductListService.resetList();
  }

  checkPage() {
    this.subscription.add(
      this.page$.subscribe(page => {
        this.search(page);
      }),
    );
  }

  getQuery(): string {
    const preventAmorphValue = (value: string) => {
      return value?.replace('amorphSelect-input', '')?.replace('amorphSelect-select', '');
    };

    let query = '';
    const currentSpeedRPM = preventAmorphValue(this.form.get('speedRPM')?.value);
    const currentOutputHP = preventAmorphValue(this.form.get('outputHP')?.value);
    const currentVoltage = preventAmorphValue(this.form.get('voltage')?.value);
    const currentFrameSize = preventAmorphValue(this.form.get('frameSize')?.value);
    const currentOutputKW = preventAmorphValue(this.form.get('outputKW')?.value);
    const currentMounting = preventAmorphValue(this.form.get('mounting')?.value);
    const currentPhase = preventAmorphValue(this.form.get('phase')?.value);
    const currentEnclosure = preventAmorphValue(this.form.get('enclosure')?.value);

    if (currentSpeedRPM) {
      query += `:relevance:speed:${currentSpeedRPM}`;
    }

    if (currentOutputHP) {
      query += `:outputHp:${currentOutputHP}`;
    }

    if (currentVoltage) {
      query += `:voltage:${currentVoltage}`;
    }

    if (currentFrameSize) {
      query += `:frameSize:${currentFrameSize}`;
    }

    if (currentOutputKW) {
      query += `:outputKw:${currentOutputKW}`;
    }

    if (currentMounting) {
      query += `:mounting:${currentMounting}`;
    }

    if (currentPhase) {
      query += `:phase:${currentPhase}`;
    }

    if (currentEnclosure) {
      query += `:enclosure:${currentEnclosure}`;
    }

    return query;
  }

  search(page: number = 0) {
    const query = this.getQuery();

    this.quoteProductListService.setSearchResult({
      ...loadingStatus,
    });

    this.quoteProductListService.showProducts$.next(true);

    this.quoteSearchProductService
      .get(query, {
        currentPage: page,
        searchQueryContext: 'FOR_QUOTES',
      })
      .pipe(take(1))
      .subscribe(searchPage => {
        if (this.updateOptions) {
          const targets = ['speed', 'outputHP', 'voltage', 'frameSize', 'outputKW', 'mounting', 'phase', 'enclosure'];
          targets.forEach(target => this.createFacets(searchPage, target));
        }

        this.checkProducts(searchPage);
      });
  }

  createFacets(searchPage: ProductSearchPage, target: string) {
    const getOptions = (facet: any): Option[] => {
      const options: Option[] = [];

      facet?.values?.forEach((el: any) => {
        const code = el.code;
        const label = el.name;

        options.push({ code, label });
      });

      return options;
    };

    const dirtOptions = searchPage.facets?.find(
      facet => facet.code?.toLocaleLowerCase() === target.toLocaleLowerCase(),
    );

    target === 'speed' && this.speedRPMs$.next(getOptions(dirtOptions));
    target === 'outputHP' && this.outputHPs$.next(getOptions(dirtOptions));
    target === 'voltage' && this.voltages$.next(getOptions(dirtOptions));
    target === 'frameSize' && this.frameSizes$.next(getOptions(dirtOptions));
    target === 'outputKW' && this.outputKWs$.next(getOptions(dirtOptions));
    target === 'mounting' && this.mountings$.next(getOptions(dirtOptions));
    target === 'phase' && this.phases$.next(getOptions(dirtOptions));
    target === 'enclosure' && this.enclosures$.next(getOptions(dirtOptions));
  }

  checkProducts(_searchPage: ProductSearchPage) {
    let searchPage = _searchPage;

    if (searchPage.products?.length === 0) {
      const productID = `
				${this.form.get('speedRPM')?.value || ''} 
				${this.form.get('outputHP')?.value || ''} 
				${this.form.get('voltage')?.value || ''} 
				${this.form.get('frameSize')?.value || ''} 
				${this.form.get('outputKW')?.value || ''} 
				${this.form.get('mounting')?.value || ''} 
				${this.form.get('phase')?.value || ''} 
				${this.form.get('enclosure')?.value || ''} 
				${this.form.get('application')?.value || ''} 
			`
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .slice(1, -1);

      searchPage = {
        ...searchPage,
        ...{
          products: [
            {
              code: productID,
              name: 'Custom Product',
              speedRPM: this.form.get('speedRPM')?.value || '',
              outputHP: this.form.get('outputHP')?.value || '',
              voltage: this.form.get('voltage')?.value || '',
              frameSize: this.form.get('frameSize')?.value || '',
              outputKW: this.form.get('outputKW')?.value || '',
              mounting: this.form.get('mounting')?.value || '',
              phase: this.form.get('phase')?.value || '',
              enclosure: this.form.get('enclosure')?.value || '',
              application: this.form.get('application')?.value || '',
              isCustom: true,
            },
          ],
        },
      };
    }

    this.quoteProductListService.setSearchResult({
      ...searchPage,
      ...successStatus,
    });
  }

  handlerSpeedRPMFormValue() {
    this.subscription.add(
      this.form.get('speedRPM')?.valueChanges.subscribe(value => {
        const $speedRPM = this.$speedRPMContainer.nativeElement as HTMLDivElement;
        const type = $speedRPM.querySelector('.js-container-amorph-select')?.getAttribute('data-type') || 'select';

        if (value === 'amorphSelect-input') {
          this.form.get('outputHP')?.setValue('amorphSelect-input');
        } else {
          this.form.get('outputHP')?.setValue('');
          type === 'select' && !isEmpty(value) && this.refineSearch('outputHP');
        }

        this.outputHPInputType = type;
      }),
    );
  }

  handlerOutputKWFormValue() {
    this.subscription.add(
      this.form.get('outputHP')?.valueChanges.subscribe(value => {
        const $outputHP = this.$outputHPContainer.nativeElement as HTMLDivElement;
        const type = $outputHP.querySelector('.js-container-amorph-select')?.getAttribute('data-type') || 'select';

        if (value === 'amorphSelect-input') {
          this.form.get('voltage')?.setValue('amorphSelect-input');
        } else {
          this.form.get('voltage')?.setValue('');
          type === 'select' && !isEmpty(value) && this.refineSearch('voltage');
        }

        this.voltageInputType = type;
      }),
    );
  }

  handlerVoltageFormValue() {
    this.subscription.add(
      this.form.get('voltage')?.valueChanges.subscribe(value => {
        const $voltage = this.$voltageContainer.nativeElement as HTMLDivElement;
        const type = $voltage.querySelector('.js-container-amorph-select')?.getAttribute('data-type') || 'select';

        if (value === 'amorphSelect-input') {
          this.form.get('frameSize')?.setValue('amorphSelect-input');
        } else {
          this.form.get('frameSize')?.setValue('');
          type === 'select' && !isEmpty(value) && this.refineSearch('frameSize');
        }

        this.frameSizeInputType = type;
      }),
    );
  }

  handlerFrameSizeFormValue() {
    this.subscription.add(
      this.form.get('frameSize')?.valueChanges.subscribe(value => {
        if (value == 'amorphSelect-input') {
          this.form.get('frameSize')?.setValue('');
        }
      }),
    );
  }

  refineSearch(target: string) {
    const query = this.getQuery();
    this.loaderService.init({ status: true, type: LOADER_TYPE.INFINITE });

    setTimeout(() => {
      this.quoteSearchProductService
        .get(query, {
          searchQueryContext: 'FOR_QUOTES',
        })
        .pipe(take(1))
        .subscribe(searchPage => {
          this.createFacets(searchPage, target);

          this.loaderService.init({ status: false, type: LOADER_TYPE.INFINITE });
        });
    }, 500);
  }

  handlerClear() {
    this.subscription.add(this.advancedSearchService.clearAdvancedSearch$.subscribe(() => this.clear()));
  }

  clear() {
    this.updateOptions = true;
    this.form.reset();

    this.clearAmorphSelect([
      'speedRPM',
      'outputHP',
      'voltage',
      'frameSize',
      'outputKW',
      'mounting',
      'phase',
      'enclosure',
    ]);

    this.quoteProductListService.resetList();
    this.quoteProductListService.showProducts$.next(false);
  }

  clearAmorphSelect(inputs: string[]) {
    inputs.forEach(input => {
      this.form.get(input)?.setValue('amorphSelect-select');
      this.form.get(input)?.setValue('');
    });
  }

  submit() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.updateOptions = false;
      this.search();
      this.quoteProductListService.showProducts$.next(true);
    }
  }
}
